import { useEffect } from 'react';

/**
 * Clear a "opening camera…" overlay when the rep comes back without a file.
 *
 * A file input fires `change` only when something is CHOSEN. Cancelling fires
 * nothing at all, so a flag set just before `.click()` and cleared only in the
 * change handler survives a cancel — and if that flag renders a full-screen
 * overlay, the app is wedged with no way out (#3448: start the camera, decide to
 * pick an existing photo instead, and the overlay covers the Gallery button).
 *
 * Returning to the page is the only reliable signal that the picker closed,
 * whichever way it closed. Clearing on the way back is safe even when a file WAS
 * chosen: the overlay's job is done either way, and the change handler still
 * runs.
 */
export function useCaptureOverlay(active: boolean, clear: () => void): void {
  useEffect(() => {
    if (!active) return;

    const onReturn = () => {
      if (document.visibilityState === 'hidden') return;
      clear();
    };

    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    // Backstop for the platforms that report neither (some in-app browsers).
    const timer = window.setTimeout(clear, 60_000);

    return () => {
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
      window.clearTimeout(timer);
    };
  }, [active, clear]);
}
